import React, { useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';

interface Props {
  onClose: () => void;
}

const COLORS = ['#8b5cf6', '#f97316', '#22d3ee', '#34d399', '#ec4899', '#fbbf24'];

const AgentMark: React.FC<{ color: string; size?: number }> = ({ color, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <g transform="translate(20,20)">
      {([0, 45, 90, 135, 180, 225, 270, 315] as const).map(a => (
        <rect key={a} x="-2.3" y="-17" width="4.6" height="11" rx="2.3"
          fill={color} transform={`rotate(${a})`} />
      ))}
    </g>
  </svg>
);

const CreateAgentPanel: React.FC<Props> = ({ onClose }) => {
  const [name, setName] = useState('');
  const [model, setModel] = useState('claude-sonnet-4-6');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(COLORS[0]);

  const handleCreate = () => {
    if (!name.trim()) return;
    window.dispatchEvent(new CustomEvent('agentCreated', {
      detail: {
        id: `agent-${Date.now()}`,
        name: name.trim(),
        description: description.trim() || 'A new Claude agent for your agency.',
        model,
        status: 'draft' as const,
        instructions: '',
        skills: [],
        mcpServers: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messageCount: 0,
        lastActive: new Date().toISOString(),
        color,
        temperature: 0.4,
        maxTokens: 4096,
      },
    }));
    onClose();
  };

  return (
    <div className="ca-create-panel">
      {/* Panel header */}
      <div className="ca-create-panel-header">
        <div className="ca-create-panel-mark">
          <AgentMark color={color} size={18} />
        </div>
        <div>
          <div className="ca-create-panel-title">New Agent</div>
          <div className="ca-create-panel-sub">Powered by Anthropic Claude</div>
        </div>
      </div>

      <div className="ca-create-panel-body">

        {/* Color preview strip */}
        <div className="ca-create-panel-preview" style={{ borderColor: `${color}35`, background: `${color}0d` }}>
          <div className="ca-create-panel-preview-avatar" style={{ background: `${color}20`, border: `1.5px solid ${color}50` }}>
            <AgentMark color={color} size={22} />
          </div>
          <div>
            <div className="ca-create-panel-preview-name">{name || 'Agent Name'}</div>
            <div className="ca-create-panel-preview-status">Draft · {
              model === 'claude-opus-4-6' ? 'Opus 4.6' :
              model === 'claude-sonnet-4-6' ? 'Sonnet 4.6' : 'Haiku 5.5'
            }</div>
          </div>
        </div>

        {/* Form fields */}
        <div className="ca-form-group">
          <label className="ca-form-label">Agent Name <span style={{ color: '#ef4444' }}>*</span></label>
          <input
            className="ca-input"
            type="text"
            placeholder="e.g. Policy Intake Agent"
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
          />
        </div>

        <div className="ca-form-group">
          <label className="ca-form-label">Model</label>
          <select
            className="ca-select"
            value={model}
            onChange={e => setModel(e.target.value)}
          >
            <option value="claude-opus-4-6">Claude Opus 4.6 — Most capable</option>
            <option value="claude-sonnet-4-6">Claude Sonnet 4.6 — Balanced (Recommended)</option>
            <option value="claude-haiku-5-5">Claude Haiku 5.5 — Fast & efficient</option>
          </select>
        </div>

        <div className="ca-form-group">
          <label className="ca-form-label">Description</label>
          <textarea
            className="ca-input"
            placeholder="What will this agent do for your agency?"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
          />
        </div>

        {/* Color picker */}
        <div className="ca-form-group">
          <label className="ca-form-label">Agent Color</label>
          <div className="ca-create-panel-colors">
            {COLORS.map(c => (
              <button
                key={c}
                className={`ca-color-swatch${color === c ? ' selected' : ''}`}
                style={{ background: c, boxShadow: color === c ? `0 0 0 2px ${c}55, 0 0 0 4px ${c}22` : 'none' }}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
        </div>

        {/* Info note */}
        <div className="ca-create-panel-note">
          <RiSparklingFill size={11} style={{ color: '#f97316', flexShrink: 0 }} />
          After creating, you'll be taken to the agent configuration page to set instructions, skills, and MCP servers.
        </div>
      </div>

      {/* Footer actions */}
      <div className="ca-create-panel-footer">
        <button className="ca-btn-ghost" onClick={onClose}>Cancel</button>
        <button
          className="ca-btn-purple"
          onClick={handleCreate}
          disabled={!name.trim()}
          style={{ opacity: !name.trim() ? 0.5 : 1 }}
        >
          <FiPlus size={13} /> Create Agent
        </button>
      </div>
    </div>
  );
};

export default CreateAgentPanel;
